import React from 'react';
import { Award, CheckCircle2, Globe, ExternalLink, Printer, Share2 } from 'lucide-react';

export default function RetirementCertificate({ certificate, onClose }) {
    if (!certificate) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="relative max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute -top-4 -right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all z-10"
                >
                    <Award size={20} className="rotate-180" />
                </button>

                {/* Certificate */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
                    {/* Certificate Header */}
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-white text-center relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
                        </div>
                        <div className="relative z-10">
                            <div className="flex justify-center mb-4">
                                <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
                                    <Award size={48} className="text-white" />
                                </div>
                            </div>
                            <h2 className="text-3xl font-black uppercase tracking-widest mb-2">Retirement Certificate</h2>
                            <p className="text-sm font-medium opacity-90">Official Proof of Carbon Credit Retirement</p>
                        </div>
                    </div>

                    {/* Certificate Body */}
                    <div className="p-8 space-y-6">
                        {/* Certificate ID */}
                        <div className="text-center pb-6 border-b border-gray-100">
                            <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-2">Certificate ID</p>
                            <p className="text-xl font-black text-gray-900 uppercase">{certificate.certificate_id}</p>
                        </div>

                        {/* Main Content */}
                        <div className="text-center space-y-4">
                            <p className="text-sm text-gray-600">This certifies that</p>
                            <p className="text-2xl font-black text-gray-900 uppercase">{certificate.beneficiary_name}</p>
                            <p className="text-sm text-gray-600">has retired</p>
                            <p className="text-5xl font-black text-emerald-600 uppercase tracking-tight">{certificate.co2_offset_tonnes.toLocaleString()} <span className="text-lg text-gray-500">tCO₂e</span></p>
                            <p className="text-sm text-gray-600">from the project</p>
                            <p className="text-xl font-bold text-gray-900 uppercase">{certificate.project_name}</p>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-100">
                            <div>
                                <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Retirement Date</p>
                                <p className="text-sm font-bold text-gray-900">{new Date(certificate.retirement_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Registry Reference</p>
                                <p className="text-sm font-bold text-gray-900">{certificate.registry_reference}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Blockchain Transaction</p>
                                <p className="text-xs font-mono font-bold text-gray-700 break-all">{certificate.blockchain_tx_hash}</p>
                            </div>
                        </div>

                        {/* QR Code and Verification */}
                        <div className="flex items-center gap-6 p-6 bg-gray-50 rounded-xl">
                            <img
                                src={certificate.qr_code_url}
                                alt="Verification QR Code"
                                className="w-24 h-24 rounded-lg border-2 border-white shadow-md"
                            />
                            <div className="flex-1">
                                <p className="text-xs font-bold text-gray-900 mb-2">Scan to Verify</p>
                                <p className="text-[10px] text-gray-600 mb-3">This certificate can be verified on the registry blockchain</p>
                                <a
                                    href={certificate.registry_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-xs font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-wider"
                                >
                                    <Globe size={14} />
                                    View on Registry
                                    <ExternalLink size={12} />
                                </a>
                            </div>
                        </div>

                        {/* Status Badge */}
                        <div className="flex items-center justify-center gap-2 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                            <CheckCircle2 className="text-emerald-600" size={20} />
                            <span className="text-sm font-black text-emerald-700 uppercase tracking-wider">Verified & Retired</span>
                        </div>
                    </div>

                    {/* Certificate Actions */}
                    <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-4">
                        <button
                            onClick={() => window.print()}
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                            <Printer size={16} />
                            Download PDF
                        </button>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                alert('Certificate link copied!');
                            }}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                            <Share2 size={16} />
                            Share
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
